import type Keycloak from 'keycloak-js';
import type { NitroFetchOptions } from 'nitropack';

// Generic Type of the API result. Data contains the actual data
export interface ApiResult<T> {
    ok: boolean;
    warnings: Warning[];
    data: T;
};

// Base Generic Type of the API response
interface BaseApiResposeType {
    warnings?: ApiSchema['WarningCollection'];
    [key: string]: any;
}

type FetchOptions = NitroFetchOptions<any>;

// Base Generic Type of the request parameters (body or query)
type BaseParamsType = Record<string, any>;

/**
 * Generic helper function to call the TOT Backend API.
 * it standardizes the way how request body or params are passed to the API
 * and how the response is transformed into the desired result.
 *
 * Uses TS generics to define the types of the result, the API response and the request parameters.
 */
export async function callApi<ResultType, ApiResponseType extends BaseApiResposeType, ParamsType extends BaseParamsType>(
    endpoint: string,
    method: 'GET' | 'POST' = 'POST',
    params: ParamsType,
    transform: (response: ApiResponseType) => ResultType,
    options?: FetchOptions,
): Promise<ApiResult<ResultType>> {
    const { $api, $keycloak } = useNuxtApp();
    const { isFeatureEnabled } = useFeatureFlags();

    if (isFeatureEnabled('login')) {
        await refreshKeycloakToken($keycloak);
    }

    const response = await $api<ApiResponseType>(endpoint, {
        method,
        ...(method === 'GET' ? { query: params } : { }),
        ...(method === 'POST' ? { body: params } : { }),
        ...options,
        headers: $keycloak?.token
            ? {
                    Authorization: `Bearer ${$keycloak?.token}`,
                    ...options?.headers,
                }
            : options?.headers,
    });

    return {
        ok: true,
        warnings: response.warnings?.warnings ?? [],
        data: transform(response as unknown as ApiResponseType),
    };
}

/**
 * Uses {@link callApi} to send a GET request to the TOT Backend API in a standardized way.
 */
export function callApiGet<ResultType, ApiResponseType extends BaseApiResposeType, ParamsType extends BaseParamsType>(
    endpoint: string,
    params: ParamsType,
    transform: (response: ApiResponseType) => ResultType,
    options?: FetchOptions,
): Promise<ApiResult<ResultType>> {
    return callApi(endpoint, 'GET', params, transform, options);
}

/**
 * Uses {@link callApi} to send a POST request to the TOT Backend API in a standardized way.
 */
export function callApiPost<ResultType, ApiResponseType extends BaseApiResposeType, ParamsType extends BaseParamsType>(
    endpoint: string,
    params: ParamsType,
    transform: (response: ApiResponseType) => ResultType,
    options?: FetchOptions,
): Promise<ApiResult<ResultType>> {
    return callApi(endpoint, 'POST', params, transform, options);
}

/**
 * Refreshes the Keycloak access token when necessary.
 * Expired or revoked sessions are cleared by Keycloak and do not need to be reported.
 */
async function refreshKeycloakToken(keycloak?: Keycloak) {
    if (!keycloak?.authenticated) {
        return;
    }

    const expirationDate = keycloak.tokenParsed?.exp ? new Date(keycloak.tokenParsed.exp * 1000) : undefined;
    const refreshExpirationDate = keycloak.refreshTokenParsed?.exp ? new Date(keycloak.refreshTokenParsed.exp * 1000) : undefined;

    try {
        await keycloak.updateToken();
    }
    catch (error) {
        const response = error && typeof error === 'object' && 'response' in error
            ? error.response
            : undefined;
        const responseStatus = response && typeof response === 'object' && 'status' in response
            ? response.status
            : undefined;

        // Keycloak clears its tokens after a rejected refresh (HTTP 400). This is a normal
        // expired/revoked session, not an application error worth reporting to Sentry.
        if (keycloak.authenticated) {
            logSilentSentryError(new Error('Error refreshing keycloak token', { cause: error }), {
                date: new Date(),
                expirationDate,
                refreshExpirationDate,
                responseStatus,
            });
        }
    }
}
